import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatDate, formatApiError } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const TABS = [
  { key: "all", label: "Todas" },
  { key: "commercial", label: "Kit Documental" },
  { key: "feature", label: "Funcionalidades" },
  { key: "lead", label: "Leads" },
];

const SOURCE_PILL = {
  commercial: "bg-emerald-50 text-emerald-900 border-emerald-300",
  feature: "bg-violet-50 text-violet-900 border-violet-300",
  lead: "bg-sky-50 text-sky-900 border-sky-300",
};

// Fase 11.5 code-quality fix #11: nested ternary → lookup table.
const SOURCE_LABEL = {
  commercial: "Kit Doc",
  feature: "Feature",
  lead: "Lead",
};

const FEAT_STATUS = ["open", "planned", "done", "rejected"];
const FEAT_PRIORITY = ["low", "medium", "high"];

export default function AdminRequestsPage() {
  const [tab, setTab] = useState("all");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // a feature request being edited

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: "1", limit: "100" });
      if (tab !== "all") params.set("source", tab);
      const { data } = await api.get(`/admin/requests?${params}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error cargando solicitudes");
    } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const counts = items.reduce((acc, i) => { acc[i.source] = (acc[i.source] || 0) + 1; return acc; }, {});

  return (
    <div data-testid="admin-requests-page">
      <p className="text-xs uppercase tracking-[0.22em] text-primary font-medium mb-1">Bandejas unificadas</p>
      <h1 className="font-serif text-3xl text-gray-900 mb-1">Solicitudes</h1>
      <p className="text-xs text-gray-500 mb-5">
        {total} entradas · Kit Documental {counts.commercial || 0} · Funcionalidades {counts.feature || 0} · Leads {counts.lead || 0}
      </p>

      <div className="border-b border-gray-200 mb-5 flex gap-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${
              tab === t.key
                ? "border-primary text-primary font-medium"
                : "border-transparent text-gray-500 hover:text-gray-900"
            }`}
            data-testid={`requests-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="border border-gray-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-[11px] uppercase tracking-widest text-gray-500 bg-gray-50 border-b">
            <th className="px-3 py-3 text-left">Tipo</th>
            <th className="px-3 py-3 text-left">Resumen</th>
            <th className="px-3 py-3 text-left">Contacto</th>
            <th className="px-3 py-3 text-left">Status</th>
            <th className="px-3 py-3 text-right">Fecha</th>
            <th className="px-3 py-3 text-right">Acciones</th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-500">Cargando…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-500" data-testid="requests-empty">
                Sin solicitudes en esta vista.
              </td></tr>
            )}
            {!loading && items.map((r) => (
              <tr key={`${r.source}-${r.id}`} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`request-row-${r.id}`}>
                <td className="px-3 py-3">
                  <span className={`inline-flex border ${SOURCE_PILL[r.source]} px-2 py-[3px] text-[10px] uppercase tracking-wider`}>
                    {SOURCE_LABEL[r.source] || r.source}
                  </span>
                </td>
                <td className="px-3 py-3 max-w-[400px]">
                  <div className="text-gray-900 truncate">{r.title}</div>
                  {r.description && <div className="text-xs text-gray-500 truncate">{r.description}</div>}
                  {r.case_id && (
                    <Link to={`/admin/cases/${r.case_id}`} className="text-[11px] text-primary hover:underline" data-testid={`request-case-link-${r.id}`}>
                      Ver caso {r.case_id.slice(0, 8)} →
                    </Link>
                  )}
                </td>
                <td className="px-3 py-3 text-xs text-gray-700 truncate max-w-[200px]">{r.user_email || "—"}</td>
                <td className="px-3 py-3 text-xs">{r.status || "—"}{r.priority ? ` · ${r.priority}` : ""}</td>
                <td className="px-3 py-3 text-xs text-gray-500 text-right whitespace-nowrap">{formatDate(r.created_at)}</td>
                <td className="px-3 py-3 text-right whitespace-nowrap">
                  {r.source === "feature" && (
                    <Button size="sm" variant="outline" className="rounded-sm h-7 text-[11px]" onClick={() => setEditing(r)} data-testid={`feature-edit-${r.id}`}>
                      Gestionar
                    </Button>
                  )}
                  {r.source === "commercial" && r.case_id && (
                    <Link to={`/admin/cases/${r.case_id}`} className="text-xs text-primary hover:underline">Abrir caso →</Link>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <FeatureEditDialog open={!!editing} item={editing} onClose={() => setEditing(null)} onSaved={load} />
    </div>
  );
}

function FeatureEditDialog({ open, item, onClose, onSaved }) {
  const [status, setStatus] = useState("open");
  const [priority, setPriority] = useState("medium");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      const raw = item.raw || {};
      setStatus(raw.status || "open");
      setPriority(raw.priority || "medium");
      setNotes(raw.admin_notes || "");
    }
  }, [item]);

  const save = async () => {
    if (!item) return;
    setSaving(true);
    try {
      await api.post(`/admin/feature-requests/${item.id}/update`, { status, priority, admin_notes: notes });
      toast.success("Solicitud actualizada");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error guardando");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none border border-gray-200">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Gestionar solicitud</DialogTitle>
          <DialogDescription className="text-xs">{item?.raw?.contact_email || item?.user_email || "Sin contacto"}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="bg-gray-50 border border-gray-200 p-3 text-sm text-gray-800 max-h-40 overflow-y-auto">
            {item?.raw?.description}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Status</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="rounded-sm h-9 text-xs" data-testid="feature-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{FEAT_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Prioridad</label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className="rounded-sm h-9 text-xs" data-testid="feature-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>{FEAT_PRIORITY.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Notas internas</label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-sm" rows={3} data-testid="feature-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose}>Cancelar</Button>
          <Button className="rounded-sm" disabled={saving} onClick={save} data-testid="feature-save">{saving ? "Guardando…" : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
